// An (i) with a short hint. Hover shows it on a mouse, a tap (or click)
// pins it open on any device, keyboard focus shows it, and Escape, a tap
// elsewhere or leaving the field closes it. A native `title` never opens
// on touch, which left every hint unreadable on a phone.
//
//   <InfoTip text="One line the spawning agents read." />   (a "\n" starts a new line)
//   <InfoTip icon={Lock} text="Built in: every team agent has it" />   (a lock that says why)
//   <InfoTip text="Looks up its data: prices…" label={<>⌁ Data API</>} />   (the thing itself is the trigger: a chip whose line shows on hover, focus or a tap)

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Info, type LucideIcon } from "lucide-react";

import { FOCUS_RING, OVERLAY_SHADOW, Z, cx } from "./controlStyles";
import { lastInputWasPointer, useAnchoredPosition, useOutsidePress } from "./overlayHooks";

export function InfoTip({ text, className, icon: Icon = Info, label }: { text: string; className?: string; icon?: LucideIcon; label?: ReactNode }) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const shown = pinned || hovered || focused;
  const pos = useAnchoredPosition(anchor, panel, shown, { matchWidth: false, offset: 6 });
  useOutsidePress([anchor, panel], pinned, () => setPinned(false));

  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The tip takes this press: what it sits in (a panel beside a list) stays open.
      e.preventDefault();
      setPinned(false);
      setHovered(false);
      setFocused(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shown]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={label ? undefined : text}
        aria-expanded={pinned}
        aria-describedby={shown ? id : undefined}
        onClick={(e) => {
          // Inside a <label> or a clickable row, the tip is its own control.
          e.preventDefault();
          e.stopPropagation();
          setPinned((p) => !p);
        }}
        onPointerEnter={(e) => e.pointerType === "mouse" && setHovered(true)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setHovered(false)}
        onFocus={() => !lastInputWasPointer() && setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setPinned(false);
        }}
        className={cx(
          label
            ? "inline-flex min-w-0 items-center text-left"
            : cx(
                "relative inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-fg-subtle [@media(hover:hover)]:hover:text-fg-muted",
                // A 32px touch target around a 14px glyph, without moving the layout.
                "after:absolute after:-inset-2 after:content-['']",
              ),
          FOCUS_RING,
          className,
        )}
      >
        {label ?? <Icon aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} />}
      </button>
      {shown &&
        createPortal(
          <div
            ref={panel}
            id={id}
            role="tooltip"
            className={cx("fixed max-w-[min(300px,calc(100vw-16px))] whitespace-pre-line rounded-control border border-line bg-overlay px-3 py-2 text-sm text-fg", OVERLAY_SHADOW)}
            style={{ zIndex: Z.popover, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
